import React from "react";
import { Route } from "react-router-dom";

// Async Component
import asyncComponent from "../components/asyncComponent/AsyncComponent";
import { APP_HASH_PATH, APP_HASH_PATH_ROUTE } from "../constants/AppGlobal";
import { sendUrlChangedPostMessage } from "../helpers/GeneralHelper";
// Layouts
import _DefaultLayout from "../layouts/DefaultLayout";
import URLHelper from "./../helpers/URLHelper";

type LayoutProps = React.PropsWithChildren<{ pageName?: string; hasHeader?: boolean }>;

// DefaultLayout is untyped (legacy class component wrapped in withRouter + connect);
const DefaultLayout = _DefaultLayout as React.ComponentType<LayoutProps>;

// Pages
const AsyncNotFound = asyncComponent(() => import("../pages/notFound/NotFound"));
const AsyncPlansPage = asyncComponent(() => import("../pages/plans/PlansPageVariation"));
const AsyncFreemiumPlansPage = asyncComponent(
	() => import("../pages/plansFreemium/FreemiumPlansPage")
);

const pathComponent = [
	{
		hash: "plans/freemium",
		component: AsyncFreemiumPlansPage,
		pageName: "Freemium Plans Page",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.PLANS_PAGE,
		component: AsyncPlansPage,
		pageName: "Plans Page",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.CHECKOUT_PAGE,
		component: asyncComponent(() => import("../pages/checkout/CheckoutPage")),
		pageName: "Checkout Summary",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {
			checkoutStep: "0",
		},
	},
	{
		hash: APP_HASH_PATH.PAYMENT_COMPLETED,
		component: asyncComponent(() => import("../pages/checkout/CheckoutCompletedPage")),
		pageName: "Payment Completed",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.PAYPAL_COMPLETED,
		component: asyncComponent(() => import("../pages/checkout/PaypalCompletedPage")),
		pageName: "Payment Completed",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.FREE_ACCOUNT_COMPLETED,
		component: asyncComponent(() => import("../pages/checkout/CheckoutCompletedPage")),
		pageName: "Free account created",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.EDU_TRIAL_ACCOUNT_COMPLETED,
		component: asyncComponent(() => import("../pages/checkout/CheckoutCompletedPage")),
		pageName: "Education Trial Account completed",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.BIZ_TRIAL_ACCOUNT_COMPLETED,
		component: asyncComponent(() => import("../pages/checkout/CheckoutCompletedPage")),
		pageName: "Business Trial Account completed",
		layout: DefaultLayout,
		exact: true,
		hasHeader: false,
		props: {},
	},
	{
		hash: APP_HASH_PATH.EMAIL_ACTIVATION,
		component: asyncComponent(() => import("../pages/activation/EmailVerificationPage")),
		pageName: "Email Verification",
		hasHeader: false,
		layout: DefaultLayout,
		exact: true,
		props: {},
	},
	{
		hash: APP_HASH_PATH.ADDITIONAL_SEATS,
		component: asyncComponent(() => import("../pages/modals/AdditionalSeatsPage")),
		pageName: "Additional seats",
		hasHeader: false,
		layout: DefaultLayout,
		exact: true,
		props: {},
	},
	{
		hash: APP_HASH_PATH.CANCEL_TRIAL,
		component: asyncComponent(() => import("../pages/modals/CancelTrialPage")),
		pageName: "Trial Cancel",
		hasHeader: false,
		layout: DefaultLayout,
		exact: true,
		props: {},
	},
	{
		hash: APP_HASH_PATH.UPGRADES_DISABLED,
		component: asyncComponent(() => import("../pages/MobileSubscriptionPage")),
		pageName: "Upgrades disabled",
		hasHeader: false,
		layout: DefaultLayout,
		exact: true,
		props: {},
	},
];

const getRouteByHash = hash => {
	const realHash = URLHelper.getRealHash(hash);

	return pathComponent.filter(function (route) {
		return route.hash === realHash;
	})[0];
};

const appRoutesHash = ({ childProps }) => {
	const { location } = childProps;

	sendUrlChangedPostMessage(location);

	const notFoundRoute = (
		<Route
			render={props => (
				<DefaultLayout pageName="Not Found" hasHeader={false}>
					<AsyncNotFound {...props} {...childProps} />
				</DefaultLayout>
			)}
		/>
	);

	if (location.pathname === APP_HASH_PATH_ROUTE && location.hash === "") {
		return notFoundRoute;
	}

	const route = getRouteByHash(location.hash);

	if (typeof route === "undefined") {
		if (location.pathname !== APP_HASH_PATH_ROUTE) {
			return null;
		}

		return notFoundRoute;
	}

	const RouteComponent = route.component;
	const Layout = route.layout;

	return (
		<Route
			path={APP_HASH_PATH_ROUTE}
			exact={route.hasOwnProperty("exact") ? route.exact : false}
			render={props => (
				<Layout pageName={route.pageName} hasHeader={route.hasHeader}>
					<RouteComponent {...props} {...childProps} />
				</Layout>
			)}
			{...route.props}
		/>
	);
};

export default appRoutesHash;
